import React, { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, Loader2, Plus, UserPlus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import CurrencyInput from '../ui/CurrencyInput';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { Express, ExpressItem } from '../../types';
import EntityModal from './EntityModal';
import { useMemo } from 'react';
import ErrorModal from './ErrorModal';
import ConfirmCloseModal from './ConfirmCloseModal';
import ConfirmModal from './ConfirmModal';
import { supabase } from '../../lib/supabase';
import { logChangesAndNotify } from '../../utils/changeLog';
import { getCurrentArgentinaISOString } from '../../utils/dateUtils';
import ChangeHistoryModal from './ChangeHistoryModal';
import { useAuth } from '../../context/AuthContext';
import PaymentReceiptField from '../shared/PaymentReceiptField';
import { isPaymentReceiptValid, notifyPaymentReceiptUploaded } from '../../utils/paymentReceipt';

interface ExpressModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId?: string | null;
}

// Define localStorage keys
const STORAGE_KEYS = {
  APPROVED_FILE: 'express_approved_file',
  DTF_FILE: 'express_dtf_file',
  PAYMENT_RECEIPT: 'express_payment_receipt',
  SHIPPING_LABEL: 'express_shipping_label',
  INVOICE_FILE: 'express_invoice_file',
  FORM_DATA: 'express_form_data',
  ITEMS: 'express_items',
  FILE_NAMES: 'express_file_names'
};

// ➕ NUEVO  ─ para saber si el borrador pertenece a este mismo pedido
const STORAGE_ORDER_ID = 'express_current_order_id';

const ExpressModal: React.FC<ExpressModalProps> = ({
  isOpen,
  onClose,
  orderId,
}) => {
  const { userRole } = useAuth();
  const hideFinancialFields = userRole === 'Producción';
  const canBypassRequiredFields = userRole === 'Producción' || userRole === 'Administrador' || userRole === 'Super Admin';
  const { 
    clients, 
    express, 
    expressItems, 
    expressStock,
    salespersons,
    addExpress, 
    updateExpress, 
    addClient, 
    addExpressStockMovement,
    fetchExpress,
    fetchExpressStock
  } = useAppStore();
  
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] = useState(false);
const [formData, setFormData] = useState<Omit<Express, 'id' | 'created_at' | 'updated_at'> & { start_date?: string }>({
  client_id: '',
  name: '',
  approved_file: '',
  dtf_file: '',
  start_date: getCurrentArgentinaISOString(),
  delivery_date: '',
  salesperson: '',
  payment_receipt: '',
  cash_payment: false,
  shipping_label: '',
  invoice_file: '',
  status: 'pending',
  comment: '',
  dtf_printed: 'No',
  dtf_picked_up: 'No',
  is_printed: false,
  express_value: null
});

  const [items, setItems] = useState<(Omit<ExpressItem, 'id' | 'express_id' | 'created_at'> & { locked?: boolean })[]>([]);
  const [originalItems, setOriginalItems] = useState<(Omit<ExpressItem, 'id' | 'express_id' | 'created_at'> & { locked?: boolean })[]>([]);
  const [isUploading, setIsUploading] = useState({
    approved: false,
    dtf: false,
    receipt: false,
    shipping: false,
    invoice: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [formModified, setFormModified] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{field: string, fileName: string} | null>(null);
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Historial de cambios
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [createdByEmail, setCreatedByEmail] = useState<string | null>(null);

  // Prepare salesperson options
  const salespersonOptions = useMemo(() => [
    { value: '', label: 'Seleccionar vendedor...' },
    ...salespersons.map(sp => ({ value: sp.name, label: `${sp.name} (${sp.email})` }))
  ], [salespersons]);

  const paymentReceiptCtx = () => ({
    hideFinancialFields,
    canBypassRequiredFields,
    userRole,
    cashPayment: Boolean(formData.cash_payment),
    paymentReceipt: formData.payment_receipt || '',
  });

  // Check if form is valid
  const isFormValid = () => {
    if (canBypassRequiredFields) return true;
    const base = formData.client_id !== '' && 
      formData.name !== '' && 
      formData.delivery_date !== '' && 
      formData.approved_file !== '' && 
      formData.dtf_printed !== null && 
      formData.dtf_picked_up !== null && 
      items.length > 0;
    if (hideFinancialFields) return base;
    return base && formData.invoice_file !== '' && isPaymentReceiptValid(paymentReceiptCtx());
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      client_id: '',
      name: '',
      approved_file: '',
      dtf_file: '',
      start_date: getCurrentArgentinaISOString(),
      delivery_date: '',
      salesperson: '',
      payment_receipt: '',
      cash_payment: false,
      shipping_label: '',
      invoice_file: '',
      status: 'pending',
      comment: '',
      dtf_printed: 'No',
      dtf_picked_up: 'No',
      is_printed: false,
      express_value: null
    });
    // Resto de la función
    setItems([]);
    setOriginalItems([]);
    setFormModified(false);
    setFileNames({});
    
    // Clear localStorage when resetting form
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    localStorage.removeItem(STORAGE_ORDER_ID);
  };

  // Load data from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      // If creating a new order, clear everything first
      if (!orderId) {
        resetForm();
        return;
      }
      
      /* 🧹 Si el borrador guardado pertenece a otro pedido, lo descarto */
      const savedOrder = localStorage.getItem(STORAGE_ORDER_ID);
      const nowOrder = orderId || 'new';            // 'new' cuando estás creando
      if (savedOrder && savedOrder !== nowOrder) {
        // Borro todos los fragmentos guardados
        Object.values(STORAGE_KEYS).forEach(k => localStorage.removeItem(k));
        localStorage.removeItem(STORAGE_ORDER_ID);
      }

      try {
        // Load files
        const approvedFile = localStorage.getItem(STORAGE_KEYS.APPROVED_FILE);
        const dtfFile = localStorage.getItem(STORAGE_KEYS.DTF_FILE);
        const paymentReceipt = localStorage.getItem(STORAGE_KEYS.PAYMENT_RECEIPT);
        const shippingLabel = localStorage.getItem(STORAGE_KEYS.SHIPPING_LABEL);
        const invoiceFile = localStorage.getItem(STORAGE_KEYS.INVOICE_FILE);
        
        // Load form data
        const savedFormData = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
        const parsedFormData = savedFormData ? JSON.parse(savedFormData) : null;
        
        // Load items
        const savedItems = localStorage.getItem(STORAGE_KEYS.ITEMS);
        const parsedItems = savedItems ? JSON.parse(savedItems) : null;
        
        // Load file names
        const savedFileNames = localStorage.getItem(STORAGE_KEYS.FILE_NAMES);
        const parsedFileNames = savedFileNames ? JSON.parse(savedFileNames) : {};
        
        if (orderId) {
          // If editing an existing order
          const order = express.find(o => o.id === orderId);
          const orderItems = expressItems.filter(i => i.express_id === orderId);
          
          if (order) {
            setFormData({
              client_id: order.client_id,
              name: order.name,
              approved_file: order.approved_file || '',
              dtf_file: order.dtf_file || '',
              invoice_file: (order as any).invoice_file || '',
              start_date: (order as any).start_date || order.created_at?.slice(0, 10) || getCurrentArgentinaISOString(),
              delivery_date: order.delivery_date,
              salesperson: order.salesperson,
              payment_receipt: order.payment_receipt || '',
              cash_payment: Boolean((order as any).cash_payment),
              shipping_label: order.shipping_label || '',
              status: order.status,
              comment: order.comment || '',
              dtf_printed: order.dtf_printed || null,
              dtf_picked_up: order.dtf_picked_up || null,
              is_printed: order.is_printed || false,
              express_value: order.express_value || null
            });

            if (order.file_names) {
              try {
                setFileNames(JSON.parse(order.file_names));
              } catch (e) {
                console.error('file_names mal formateado', e);
              }
            }

            // When editing, all existing items are locked
            const itemsData = orderItems.map(item => ({
              product_type: item.product_type,
              color: item.color,
              size: item.size,
              quantity: item.quantity,
              stock_id: item.stock_id,
              locked: true
            }));

            setItems(itemsData);
            setOriginalItems(JSON.parse(JSON.stringify(itemsData))); // Deep copy for comparison
            
            // Check if we have saved changes for this order
            if (parsedFormData && parsedItems) {
              // Apply saved changes on top of the database data
              setFormData(prev => ({
                ...prev,
                ...(parsedFormData || {}),
                approved_file: approvedFile || prev.approved_file,
                dtf_file: dtfFile || prev.dtf_file,
                payment_receipt: paymentReceipt || prev.payment_receipt,
                shipping_label: shippingLabel || prev.shipping_label,
                invoice_file: invoiceFile || prev.invoice_file,
              }));
              
              // Apply saved items
              setItems(parsedItems);
              
              // Apply saved file names
              if (parsedFileNames) {
                setFileNames(parsedFileNames);
              }
              
              setFormModified(true);
            }
          }
        } else {
          // If creating a new order
          // Update state with saved data
          setFormData(prev => ({
            ...prev,
            ...(parsedFormData || {}),
            approved_file: approvedFile || '',
            dtf_file: dtfFile || '',
            payment_receipt: paymentReceipt || '',
            shipping_label: shippingLabel || '',
            invoice_file: invoiceFile || '',
          }));
          
          // Apply saved items
          if (parsedItems) {
            setItems(parsedItems);
          }
          
          // Apply saved file names
          if (parsedFileNames) {
            setFileNames(parsedFileNames);
          }
        }

        // Reset form modified flag
        setFormModified(false);
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
        // If there's an error, just continue with empty form
      }
    }
  }, [isOpen, orderId]);

  // Obtener creador
  useEffect(() => {
    const fetchCreator = async () => {
      if (!orderId) return;
      const { data } = await supabase
        .from('entity_change_log')
        .select('changed_by_email, created_at')
        .eq('entity_type', 'express_order')
        .eq('entity_id', orderId)
        .order('created_at', { ascending: true })
        .limit(1);
      const emailFromLog = data?.[0]?.changed_by_email || null;
      if (emailFromLog) {
        setCreatedByEmail(emailFromLog);
      } else {
        const order = express.find(e => e.id === orderId);
        const sp = salespersons.find(sp => sp.name === order?.salesperson);
        setCreatedByEmail(sp?.email || null);
      }
    };
    fetchCreator();
  }, [orderId]);

  useEffect(() => {
    if (!isHistoryOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsHistoryOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isHistoryOpen]);

  // Save form data to localStorage when it changes
  useEffect(() => {
    if (isOpen && formModified) {
      try {
        // Save form data without the file URLs (we store those separately)
        const formDataToSave = {
          ...formData,
          approved_file: undefined,
          dtf_file: undefined,
          payment_receipt: undefined,
          shipping_label: undefined,
          invoice_file: undefined,
        };
        /* ➕ Guardar a qué pedido pertenece este borrador */
localStorage.setItem(STORAGE_ORDER_ID, orderId || 'new');

        localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formDataToSave));
        
        // Save items (without the locked status)
        const itemsToSave = items.map(({ locked, ...item }) => item);
        localStorage.setItem(STORAGE_KEYS.ITEMS, JSON.stringify(itemsToSave));
        
        // Save file URLs separately
        if (formData.approved_file) {
          localStorage.setItem(STORAGE_KEYS.APPROVED_FILE, formData.approved_file);
        }
        
        if (formData.dtf_file) {
          localStorage.setItem(STORAGE_KEYS.DTF_FILE, formData.dtf_file);
        }
        
        if (formData.payment_receipt) {
          localStorage.setItem(STORAGE_KEYS.PAYMENT_RECEIPT, formData.payment_receipt);
        }
        
        if (formData.shipping_label) {
          localStorage.setItem(STORAGE_KEYS.SHIPPING_LABEL, formData.shipping_label);
        }
        
        if (formData.invoice_file) {
          localStorage.setItem(STORAGE_KEYS.INVOICE_FILE, formData.invoice_file);
        }
        
        // Save file names
        localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify(fileNames));
      } catch (error) {
        console.error('Error saving data to localStorage:', error);
        // If there's an error saving, just continue
      }
    }
  }, [formData, items, isOpen, formModified, fileNames]);

  // Handle form changes
  const handleFormChange = (newFormData: typeof formData) => {
    setFormData(newFormData);
    setFormModified(true);
  };

// Función para manejar el cambio del valor de la produccion
const handleExpressValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  // Si el campo está vacío, establecer null, de lo contrario, convertir a número
  const numericValue = value === '' ? null : parseFloat(value);
  
  handleFormChange({
    ...formData,
    express_value: numericValue
  });
};

  // Clear localStorage and close modal
  const handleConfirmClose = () => {
    // Clear localStorage when modal is closed
    localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
    localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
    localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
    localStorage.removeItem(STORAGE_KEYS.SHIPPING_LABEL);
    localStorage.removeItem(STORAGE_KEYS.INVOICE_FILE);
    localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
    localStorage.removeItem(STORAGE_KEYS.ITEMS);
    localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
    localStorage.removeItem(STORAGE_ORDER_ID);
    
    // Reset form
    resetForm();
    
    // Reset form modified flag
    setFormModified(false);
    
    // Close confirm modal
    setIsConfirmCloseModalOpen(false);
    
    // Call the original onClose function
    onClose();
  };

  // Handle close button click
  const handleCloseClick = () => {
    // If form is not modified, just close
    if (!formModified) {
      resetForm();
      onClose();
      return;
    }
    
    // If form is modified, show confirm dialog
    setIsConfirmCloseModalOpen(true);
  };

  const handleFileUpload = async (files: FileList, type: 'approved' | 'dtf' | 'receipt' | 'shipping' | 'invoice') => {
    const uploadStateMap = {
      approved: 'approved_file',
      dtf: 'dtf_file',
      receipt: 'payment_receipt',
      shipping: 'shipping_label',
      invoice: 'invoice_file',
    };

    const storageKeyMap = {
      approved: STORAGE_KEYS.APPROVED_FILE,
      dtf: STORAGE_KEYS.DTF_FILE,
      receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
      shipping: STORAGE_KEYS.SHIPPING_LABEL,
      invoice: STORAGE_KEYS.INVOICE_FILE,
    };

    try {
      setIsUploading(prev => ({ ...prev, [type]: true }));

      const uploadedUrls: string[] = [];
      const uploadedFileNames: string[] = [];

      // Process each file in the FileList
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Create a unique file name
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${type}/${fileName}`;

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('express-files')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('express-files')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        uploadedFileNames.push(file.name);
      }

      // Save the original file names
      setFileNames(prev => ({
        ...prev,
        [uploadStateMap[type]]: uploadedFileNames
      }));

      // Join multiple URLs with a delimiter if there are multiple files
      const urlString = uploadedUrls.join('|');

      handleFormChange({
        ...formData,
        [uploadStateMap[type]]: urlString,
      });

      // Save to localStorage
      localStorage.setItem(storageKeyMap[type], urlString);
      
      // Save file names to localStorage
      localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
        ...fileNames,
        [uploadStateMap[type]]: uploadedFileNames
      }));

      if (type === 'receipt') {
        const clientName = clients.find((c) => c.id === formData.client_id)?.name || '—';
        await notifyPaymentReceiptUploaded({
          entityType: 'express',
          entityId: orderId || null,
          entityName: formData.name || 'Pedido Express',
          clientName,
          receiptUrl: urlString,
          salesperson: formData.salesperson,
        });
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Error al subir el archivo');
      setShowErrorModal(true);
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDeleteFile = (field: string) => {
    // Extract file names from URL string
    const urls = (formData[field as keyof typeof formData] as string).split('|');
    const fileNamesArray = fileNames[field] || urls.map(url => url.split('/').pop() || 'archivo');
    
    setFileToDelete({ field, fileName: fileNamesArray.join(', ') });
    setIsDeleteFileModalOpen(true);
  };

  const confirmDeleteFile = () => {
    if (!fileToDelete) return;
    
    // Update form data
    handleFormChange({
      ...formData,
      [fileToDelete.field]: '',
    });
    
    // Remove from localStorage
    const storageKeyMap: Record<string, string> = {
      approved_file: STORAGE_KEYS.APPROVED_FILE,
      dtf_file: STORAGE_KEYS.DTF_FILE,
      payment_receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
      shipping_label: STORAGE_KEYS.SHIPPING_LABEL,
      invoice_file: STORAGE_KEYS.INVOICE_FILE,
    };
    
    const storageKey = storageKeyMap[fileToDelete.field];
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    
    // Remove from fileNames
    setFileNames(prev => {
      const newFileNames = {...prev};
      delete newFileNames[fileToDelete.field];
      return newFileNames;
    });
    
    // Update fileNames in localStorage
    localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
      ...fileNames,
      [fileToDelete.field]: undefined
    }));
    
    setFileToDelete(null);
    setFormModified(true);
  };

  const getFileNameFromUrl = (url: string, field: string): string => {
    // First check if we have the original filenames stored
    if (fileNames[field] && fileNames[field].length > 0) {
      return fileNames[field].join(', ');
    }
    
    // Otherwise extract from URL
    const urls = url.split('|');
    return urls.map(u => {
      const parts = u.split('/');
      return parts[parts.length - 1] || 'archivo';
    }).join(', ');
  };

  // Render file links for multiple files
const renderFileLinks = (urls: string, field: string) => {
  if (!urls) return null;

  const urlArray = urls.split('|');
  const fileNamesArray = fileNames[field] || [];

  return (
    <div className="flex flex-col gap-1">
      {urlArray.map((url, index) => (
        <a
          key={index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          {fileNamesArray[index] || url.split('/').pop() || `Archivo ${index + 1}`}
        </a>
      ))}
    </div>
  );
};



  // Get available product types from stock
  const getAvailableProductTypes = () => {
    return [...new Set(expressStock
      .filter(stock => stock.quantity > 0)
      .map(stock => stock.product_type)
    )];
  };

  // Get available colors for a product type
  const getAvailableColors = (productType: string) => {
    return [...new Set(expressStock
.filter(stock =>
  stock.product_type === productType &&
  (stock.quantity > 0 || items.some(item =>
    item.product_type === stock.product_type &&
    item.color === stock.color
  ))
)
      .map(stock => stock.color)
    )];
  };

  // Get available sizes for a product type and color
    const getAvailableSizes = (productType: string, color: string) => {
      return expressStock
  .filter(stock => 
    stock.product_type === productType && 
    stock.color === color &&
    (stock.quantity > 0 || items.some(item =>
      item.product_type === stock.product_type &&
      item.color === stock.color &&
      item.size === stock.size
    ))
  )
      .map(stock => ({
        value: stock.size,
        label: `${stock.size} (${stock.quantity} disponibles)`
      }));
  };

  // Get stock ID for a product type, color, and size
  const getStockId = (productType: string, color: string, size: string) => {
    const stock = expressStock.find(s => 
      s.product_type === productType && 
      s.color === color && 
      s.size === size
    );
    return stock?.id || '';
  };

const handleAddItem = () => {
  const availableProductTypes = getAvailableProductTypes();
  if (availableProductTypes.length === 0) {
    setError('No hay productos disponibles en stock');
    setShowErrorModal(true);
    return;
  }

  // Bloquear todos los ítems existentes
  const updatedItems = items.map(item => ({
    ...item,
    locked: true
  }));

  // Agregar un nuevo ítem desbloqueado
  const newItems = [
    ...updatedItems,
    {
      product_type: '',
      color: '',
      size: '',
      quantity: 1,
      stock_id: '',
      locked: false
    }
  ];

  setItems(newItems);
  setFormModified(true);
};



  const handleUpdateItem = (index: number, field: keyof ExpressItem, value: any) => {
    // If the item is locked, don't allow updates
    if (items[index].locked) {
      return;
    }
    
    // Create a new array to avoid mutating the original
    const updatedItems = [...items];
    
    // If changing product_type, reset color and size
if (field === 'product_type') {
  updatedItems[index] = {
    ...updatedItems[index],
    [field]: value,
    color: '',
    size: '',
    stock_id: ''
  };
}

    // If changing size
    else if (field === 'size') {
      const stockId = getStockId(updatedItems[index].product_type, updatedItems[index].color, value);
      
      // Check if this combination already exists in other items
      const itemExists = items.some((item, i) => 
        i !== index && 
        item.product_type === updatedItems[index].product_type && 
        item.color === updatedItems[index].color && 
        item.size === value
      );
      
      if (itemExists) {
        setError(`Ya existe un item con ${updatedItems[index].product_type} color ${updatedItems[index].color} talle ${value}`);
        setShowErrorModal(true);
        return;
      }
      
      updatedItems[index] = { 
        ...updatedItems[index], 
        [field]: value,
        stock_id: stockId
      };
    }
    // If changing quantity, check if there's enough stock
    else if (field === 'quantity') {
      const stockItem = expressStock.find(s => s.id === updatedItems[index].stock_id);
      
      if (stockItem && value > stockItem.quantity) {
        setError(`No hay suficiente stock disponible. Máximo: ${stockItem.quantity}`);
        setShowErrorModal(true);
        return;
      }
      
      updatedItems[index] = { 
        ...updatedItems[index], 
        [field]: value 
      };
    }
    // Otherwise just update the field
    else {
      updatedItems[index] = { 
        ...updatedItems[index], 
        [field]: value 
      };
    }
    
    setItems(updatedItems);
    setFormModified(true);
  };

  const handleRemoveItem = (index: number) => {
    // Create a new array without the item at the specified index
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    
    // If we're removing the last item and there are still items left,
    // and we're NOT editing an existing order, unlock the new last item
    if (updatedItems.length > 0 && !orderId) {
      updatedItems[updatedItems.length - 1].locked = false;
    }
    
    setItems(updatedItems);
    setFormModified(true);
  };

  // Calculate stock changes between original and current items
  const calculateStockChanges = () => {
    const changes: {
      stockId: string;
      quantity: number;
      type: 'in' | 'out';
    }[] = [];

    // If creating a new order, all items are "out" movements
    if (!orderId) {
      items.forEach(item => {
        if (item.stock_id) {
          changes.push({
            stockId: item.stock_id,
            quantity: item.quantity,
            type: 'out'
          });
        }
      });
      return changes;
    }

    // For editing, we need to compare original and current items
    
    // First, handle removed items (return to stock)
    originalItems.forEach(originalItem => {
      const stillExists = items.some(
        item => 
          item.product_type === originalItem.product_type &&
          item.color === originalItem.color &&
          item.size === originalItem.size
      );

      if (!stillExists && originalItem.stock_id) {
        changes.push({
          stockId: originalItem.stock_id,
          quantity: originalItem.quantity,
          type: 'in' // Return to stock
        });
      }
    });

    // Then handle new or modified items
    items.forEach(item => {
      const originalItem = originalItems.find(
        oi => 
          oi.product_type === item.product_type &&
          oi.color === item.color &&
          oi.size === item.size
      );

      if (!item.stock_id) return;

      if (!originalItem) {
        // New item - take from stock
        changes.push({
          stockId: item.stock_id,
          quantity: item.quantity,
          type: 'out'
        });
      } else if (item.quantity !== originalItem.quantity) {
        // Quantity changed
        const diff = item.quantity - originalItem.quantity;
        if (diff > 0) {
          // Need more from stock
          changes.push({
            stockId: item.stock_id,
            quantity: diff,
            type: 'out'
          });
        } else if (diff < 0) {
          // Return some to stock
          changes.push({
            stockId: item.stock_id,
            quantity: Math.abs(diff),
            type: 'in'
          });
        }
      }
    });

    return changes;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields (administradores pueden omitir)
    if (!canBypassRequiredFields) {
      if (!formData.approved_file) {
        setError('Debe cargar la ficha aprobada');
        setShowErrorModal(true);
        return;
      }
      if (!hideFinancialFields && !formData.invoice_file) {
        setError('El archivo de factura es obligatorio');
        setShowErrorModal(true);
        return;
      }
      if (!isPaymentReceiptValid(paymentReceiptCtx())) {
        setError('El comprobante de pago es obligatorio');
        setShowErrorModal(true);
        return;
      }
    }
    
    // Validate stock availability for all items (administradores pueden omitir)
    if (!canBypassRequiredFields) {
    for (const item of items) {
      const stockItem = expressStock.find(s => s.id === item.stock_id);
      
      // For new items or increased quantities, check stock availability
      const originalItem = originalItems.find(oi => 
        oi.product_type === item.product_type && 
        oi.color === item.color && 
        oi.size === item.size
      );
      
      let requiredQuantity = item.quantity;
      
      // If editing an existing item, only check the difference
      if (originalItem) {
        requiredQuantity = Math.max(0, item.quantity - originalItem.quantity);
      }
      
      if (!stockItem || stockItem.quantity < requiredQuantity) {
        setError(`No hay suficiente stock para ${item.product_type} ${item.color} talle ${item.size}`);
        setShowErrorModal(true);
        return;
      }
    }
    }

    try {
      // Calculate stock changes
      const stockChanges = calculateStockChanges();

      // Remove locked property from items before saving
      const itemsToSave = items.map(({ locked, ...item }) => item);

      if (orderId) {
        // Update existing order
        const payload = {
  ...formData,
  items: itemsToSave,
  file_names: JSON.stringify(fileNames),   // 👈 NUEVO
};

        // Registrar cambios y notificar
        const prev = express.find(o => o.id === orderId) as any;
        if (prev) {
          await logChangesAndNotify({
            entityType: 'express_order',
            entityId: orderId,
            entityName: prev.name,
            previous: prev,
            next: { ...prev, ...payload } as any,
            includeFields: ['name','client_id','delivery_date','status','salesperson','express_value','comment','approved_file','dtf_file','payment_receipt','shipping_label','invoice_file']
          });
        }

        await updateExpress(orderId, payload);

        
        // Process stock changes
        for (const change of stockChanges) {
          // Create stock movement
          await addExpressStockMovement({
            stock_id: change.stockId,
            movement_type: change.type,
            quantity: change.quantity,
            express_id: orderId
          });
          
          // Update stock quantity
          const stockItem = expressStock.find(s => s.id === change.stockId);
          if (stockItem) {
            const newQuantity = change.type === 'in' 
              ? stockItem.quantity + change.quantity
              : Math.max(0, stockItem.quantity - change.quantity);
            
            // Update stock in database
            const { error } = await supabase
              .from('express_stock')
              .update({ quantity: newQuantity })
              .eq('id', change.stockId);
            
            if (error) throw error;
          }
        }
      } else {
        // Create new order
        const newOrderId = await addExpress(
  { ...formData, file_names: JSON.stringify(fileNames) },   // 👈 NUEVO
  itemsToSave
);


/* 🚨 abortar si no volviera un id real */
if (!newOrderId) {
  throw new Error('El pedido no se guardó en la base');
}

        
        // Process stock movements for each item
        for (const change of stockChanges) {
          // Create stock movement
          await addExpressStockMovement({
            stock_id: change.stockId,
            movement_type: change.type,
            quantity: change.quantity,
            express_id: newOrderId
          });
          
          // Update stock quantity
          const stockItem = expressStock.find(s => s.id === change.stockId);
          if (stockItem) {
            const newQuantity = change.type === 'in' 
              ? stockItem.quantity + change.quantity
              : Math.max(0, stockItem.quantity - change.quantity);
            
            // Update stock in database
            const { error } = await supabase
              .from('express_stock')
              .update({ quantity: newQuantity })
              .eq('id', change.stockId);
            
            if (error) throw error;
          }
        }
      }
      
      // Refresh stock data after changes
      await fetchExpressStock();
      await fetchExpress();        // 👈  refresca la lista completa
      
      // Clear localStorage after successful submission
      localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
      localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
      localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
      localStorage.removeItem(STORAGE_KEYS.SHIPPING_LABEL);
      localStorage.removeItem(STORAGE_KEYS.INVOICE_FILE);
      localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
      localStorage.removeItem(STORAGE_KEYS.ITEMS);
      localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
      
      // Reset form
      resetForm();
      
      // Reset form modified flag
      setFormModified(false);
      
      onClose();
    } catch (error: any) {
      console.error('Error saving express order:', error);
      setError(error.message || 'Error al guardar el pedido');
      setShowErrorModal(true);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleCloseClick}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 max-h-[90vh] rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none flex flex-col min-h-0 z-50">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {orderId ? 'Editar Pedido Express' : 'Nuevo Pedido Express'}
                  </h2>
                  {orderId && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {(() => {
                        const createdAt = (express.find(e => e.id === orderId)?.created_at) || '';
                        return createdAt ? `Creado: ${new Date(createdAt).toLocaleString()} · ${createdByEmail || 'usuario desconocido'}` : '';
                      })()}
                    </p>
                  )}
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600" aria-label="Cerrar" onClick={(e) => { e.preventDefault(); handleCloseClick(); }}>
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Cliente *</label>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 min-w-0 [&>div]:!mb-0">
                        <Select
                          label=""
                          value={formData.client_id}
                          onChange={(value) => handleFormChange({ ...formData, client_id: value })}
                          options={[{ value: '', label: 'Seleccionar cliente...' }, ...clients.map(client => ({ value: client.id, label: client.name }))]}
                          required
                        />
                      </div>
                      <Button type="button" variant="outline" onClick={() => setIsClientModalOpen(true)} icon={<UserPlus className="h-4 w-4" />} className="h-12 shrink-0 self-end">Nuevo</Button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Nombre del Pedido *</label>
                    <input type="text" placeholder="Ej: Remeras estampadas" value={formData.name} onChange={(e) => handleFormChange({ ...formData, name: e.target.value })}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="[&>div]:!mb-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Tipo</label>
                    <Select label="" value={formData.is_printed ? "true" : "false"} onChange={(value) => handleFormChange({ ...formData, is_printed: value === "true" })} options={[{ value: "false", label: "Liso" }, { value: "true", label: "Estampado" }]} required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Fecha de inicio *</label>
                    <input type="date" value={formData.start_date || ''} onChange={(e) => handleFormChange({ ...formData, start_date: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Fecha de entrega *</label>
                    <input type="date" value={formData.delivery_date} onChange={(e) => handleFormChange({ ...formData, delivery_date: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" required />
                  </div>
                  <div className="[&>div]:!mb-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Vendedor *</label>
                    <Select label="" value={formData.salesperson} onChange={(value) => handleFormChange({ ...formData, salesperson: value })} options={salespersonOptions} required />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Ficha Aprobada *</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'approved'); }} className="hidden" id="approved-file" multiple />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('approved-file')?.click()} disabled={isUploading.approved}
                        icon={isUploading.approved ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                        {formData.approved_file ? 'Cambiar' : 'Subir Ficha'}
                      </Button>
                      {formData.approved_file && (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {renderFileLinks(formData.approved_file, 'approved_file')}
                          <button type="button" onClick={() => handleDeleteFile('approved_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                    {!formData.approved_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                  </div>
                  {!hideFinancialFields && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Factura *</span>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'invoice'); }} className="hidden" id="invoice-file" multiple />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('invoice-file')?.click()} disabled={isUploading.invoice}
                        icon={isUploading.invoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                        {formData.invoice_file ? 'Cambiar' : 'Subir Factura'}
                      </Button>
                      {formData.invoice_file && (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {renderFileLinks(formData.invoice_file, 'invoice_file')}
                          <button type="button" onClick={() => handleDeleteFile('invoice_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                    {!formData.invoice_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                  </div>
                  )}
                </div>

                {(hideFinancialFields && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Ficha Aprobada *</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'approved'); }} className="hidden" id="approved-file" multiple />
                    <Button type="button" variant="outline" onClick={() => document.getElementById('approved-file')?.click()} disabled={isUploading.approved}
                      icon={isUploading.approved ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                      {formData.approved_file ? 'Cambiar' : 'Subir Ficha'}
                    </Button>
                    {formData.approved_file && (
                      <div className="flex items-center gap-1 min-w-0 flex-1">
                        {renderFileLinks(formData.approved_file, 'approved_file')}
                        <button type="button" onClick={() => handleDeleteFile('approved_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                  {!formData.approved_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                </div>
                )) || null}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="[&>div]:!mb-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">DTF enviado</label>
                    <Select label="" value={formData.dtf_printed || 'No'} onChange={(value) => handleFormChange({ ...formData, dtf_printed: value as 'Sí' | 'No' })} options={[{ value: 'No', label: 'No' }, { value: 'Sí', label: 'Sí' }]} required />
                  </div>
                  <div className="[&>div]:!mb-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">DTF retirado</label>
                    <Select label="" value={formData.dtf_picked_up || 'No'} onChange={(value) => handleFormChange({ ...formData, dtf_picked_up: value as 'Sí' | 'No' })} options={[{ value: 'No', label: 'No' }, { value: 'Sí', label: 'Sí' }]} required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Archivo DTF</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'dtf'); }} className="hidden" id="dtf-file" multiple />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('dtf-file')?.click()} disabled={isUploading.dtf}
                        icon={isUploading.dtf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm">Subir DTF</Button>
                      {formData.dtf_file && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.dtf_file, 'dtf_file')}</div><button type="button" onClick={() => handleDeleteFile('dtf_file')} className="text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></>)}
                    </div>
                  </div>
                </div>

                {!hideFinancialFields && (
                <CurrencyInput label="Valor de la Producción" value={formData.express_value} onChange={(value) => { setFormData(prev => ({ ...prev, express_value: value ? parseFloat(value) : null })); setFormModified(true); }} id="express_value" name="express_value" required className="!mb-0" />
                )}

                {/* Items */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Items</h3>
                    
                  </div>

                  {items.map((item, index) => (
                    <div 
                      key={index} 
                      className={`grid grid-cols-12 gap-2 items-start border ${
                        item.locked 
                          ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50' 
                          : 'border-gray-200 dark:border-gray-700'
                      } p-4 rounded-lg`}
                    >
                      <div className="col-span-3">
<Select
  label="Tipo"
  value={item.product_type}
  onChange={(value) => handleUpdateItem(index, 'product_type', value)}
  options={[
    { value: '', label: 'Seleccione tipo' },
    ...getAvailableProductTypes().map(type => ({
      value: type,
      label: type
    }))
  ]}
  required
  disabled={item.locked}
/>

                      </div>

                      <div className="col-span-3">
<Select
  label="Color"
  value={item.color}
  onChange={(value) => handleUpdateItem(index, 'color', value)}
  options={[
    { value: '', label: 'Seleccione color' },
    ...(item.product_type ? 
      getAvailableColors(item.product_type).map(color => ({
        value: color,
        label: color
      })) : [])
  ]}
  required
  disabled={item.locked || !item.product_type}
/>

                      </div>

                      <div className="col-span-3">
<Select
  label="Talle"
  value={item.size}
  onChange={(value) => handleUpdateItem(index, 'size', value as 'S' | 'M' | 'L' | 'XL')}
  options={[
    { value: '', label: 'Seleccione talle' },
    ...(item.product_type && item.color ? 
      getAvailableSizes(item.product_type, item.color) : [])
  ]}
  required
  disabled={item.locked || !item.product_type || !item.color}
/>

                      </div>

                      <div className="col-span-2">
                        <Input
                          label="Cantidad"
                          type="number"
                          id={`quantity-${index}`}
                          name={`quantity-${index}`}
                          value={item.quantity.toString()}
                          onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value))}
                          min="1"
                          required
                          disabled={item.locked}
                        />
                      </div>

                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="mt-7 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-none"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
<Button
                      variant="outline"
                      size="sm"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={handleAddItem}
                    >
                      Agregar Item
                    </Button>

                <div className="pt-2">
                  <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
                    {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    Más opciones
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-100 dark:border-gray-800">
                      <PaymentReceiptField
                        inputId="payment-receipt"
                        paymentReceipt={formData.payment_receipt}
                        cashPayment={Boolean(formData.cash_payment)}
                        userRole={userRole}
                        hideFinancialFields={hideFinancialFields}
                        canBypassRequiredFields={canBypassRequiredFields}
                        isUploading={isUploading.receipt}
                        onUpload={(files) => handleFileUpload(files, 'receipt')}
                        onDelete={() => handleDeleteFile('payment_receipt')}
                        onCashPaymentChange={(checked) => handleFormChange({ ...formData, cash_payment: checked })}
                        renderFileLinks={renderFileLinks}
                      />
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Etiqueta de envío</span>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'shipping'); }} className="hidden" id="shipping-label" multiple />
                          <Button type="button" variant="outline" onClick={() => document.getElementById('shipping-label')?.click()} disabled={isUploading.shipping}
                            icon={isUploading.shipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm">Subir Etiqueta</Button>
                          {formData.shipping_label && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.shipping_label, 'shipping_label')}</div><button type="button" onClick={() => handleDeleteFile('shipping_label')} className="text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></>)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Comentarios</span>
                        <textarea id="comment" name="comment" rows={2} value={formData.comment}
                          onChange={(e) => handleFormChange({ ...formData, comment: e.target.value })}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400" placeholder="Opcional..."
                        />
                      </div>
                      {orderId && (
                        <Select label="Estado" value={formData.status} onChange={(value) => handleFormChange({ ...formData, status: value as Express['status'] })}
                          options={[{ value: 'pending', label: 'Pendiente' }, { value: 'in_progress', label: 'En Proceso' }, { value: 'finished', label: 'Terminado' }, { value: 'shipped', label: 'Despachado' }]} required
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2 shrink-0">
                {orderId ? (
                  <button
                    type="button"
                    className="text-xs underline text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={async () => {
                      setIsHistoryOpen(true);
                      setIsLoadingHistory(true);
                      const { data } = await supabase
                        .from('entity_change_log')
                        .select('*')
                        .eq('entity_type', 'express_order')
                        .eq('entity_id', orderId)
                        .order('created_at', { ascending: false });
                      setHistoryItems(data || []);
                      setIsLoadingHistory(false);
                    }}
                  >
                    Historial de cambios
                  </button>
                ) : <span />}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseClick}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid()}
                >
                  {orderId ? 'Guardar Cambios' : 'Crear Pedido'}
                </Button>
              </div>
            </form>
            
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ChangeHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        entityType="express_order"
        entityId={orderId}
        createdAt={(express.find(e => e.id === orderId)?.created_at) || null}
        createdByEmail={createdByEmail || undefined}
      />

      {isClientModalOpen && (
        <EntityModal
          isOpen={isClientModalOpen}
          onClose={() => setIsClientModalOpen(false)}
          entityType="client"
          onSave={async (client) => {
            try {
              const newClient = await addClient(client);
              handleFormChange({ ...formData, client_id: newClient.id });
              setIsClientModalOpen(false);
              setFormModified(true);
            } catch (error) {
              console.error('Error creating client:', error);
              setError('Error al crear el cliente');
              setShowErrorModal(true);
            }
          }}
        />
      )}

      <ConfirmCloseModal
        isOpen={isConfirmCloseModalOpen}
        onClose={() => setIsConfirmCloseModalOpen(false)}
        onConfirm={handleConfirmClose}
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />

      <ConfirmModal
        isOpen={isDeleteFileModalOpen}
        onClose={() => {
          setIsDeleteFileModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={confirmDeleteFile}
        title="Eliminar archivo"
        message={`¿Estás seguro de que querés eliminar el archivo ${fileToDelete?.fileName}?`}
        confirmText="Eliminar"
        variant="danger"
      />
    </>
  );
};

export default ExpressModal;